import type { Product, Teasers } from "@decocms/apps-commerce/types";
import { useOffer } from "~/sdk/useOffer";

function getPriceFromTeaser(teaser: Teasers | undefined) {
  if (!teaser) return;

  return (
    Number(
      teaser.effects.parameters.find(
        (p) => p.name === "MaximumUnitPriceDiscount"
      )?.value ?? 0
    ) / 100
  );
}

function findTeaserWith(teasers: Teasers[], ...terms: string[]) {
  return teasers.find((s) => {
    const name = s.name.toLowerCase();

    return terms.every((term) => name.includes(term.toLocaleLowerCase()));
  });
}

function findAllTeasersWith(teasers: Teasers[], ...terms: string[]) {
  return teasers.filter((s) => {
    const name = s.name.toLowerCase();
    return terms.every((term) => name.includes(term.toLocaleLowerCase()));
  });
}

function minPriceFromMatchingTeasers(teasers: Teasers[], ...terms: string[]) {
  const prices = findAllTeasersWith(teasers, ...terms)
    .map((t) => getPriceFromTeaser(t))
    .filter((p): p is number => p !== undefined && p > 0);

  if (!prices.length) return undefined;
  return Math.min(...prices);
}

function applyDiscount(price: number, teaser: Teasers | undefined) {
  if (!teaser) return undefined;
  const discount = teaser.effects.parameters.find(
    (p) => p.name === "PercentualDiscount"
  )?.value;

  if (!discount) return undefined;
  const percentage = Number(discount);

  return price - (price * percentage) / 100;
}

export function getFinalPrice(offers: Product["offers"]) {
  const { price = 0 } = useOffer(offers);
  const teasers = offers?.offers[0]?.teasers ?? [];

  const fixedPrice = findTeaserWith(teasers, "fixo");
  const fixedPixPrice = minPriceFromMatchingTeasers(teasers, "pix", "fixo");
  const pixPrice = applyDiscount(price, findTeaserWith(teasers, "no", "pix"));
  const boletoPrice = applyDiscount(price, findTeaserWith(teasers, "boleto"));
  const pixWithoutPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "pix", "sem", "prime")
  );
  const pixWithPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "pix", "com", "prime")
  );
  const cardWithPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "cartão", "com", "prime")
  );
  const cardWithoutPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "cartão", "sem", "prime")
  );
  const BLandTFWithPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "BL", "TF", "com", "prime")
  );
  const BLandTFWithoutPrime = applyDiscount(
    price,
    findTeaserWith(teasers, "BL", "TF", "sem", "prime")
  );

  let finalPrice = 0;
  let discountType = "";
  if (fixedPixPrice) {
    finalPrice = fixedPixPrice;
    discountType = "pix";
  } else if (fixedPrice) finalPrice = price;
  else if (pixPrice) {
    finalPrice = pixPrice;
    discountType = "pix";
  } else if (pixWithoutPrime) {
    finalPrice = pixWithoutPrime;
    discountType = "pix";
  } else if (pixWithPrime) {
    finalPrice = pixWithPrime;
    discountType = "pix";
  } else if (cardWithoutPrime) finalPrice = cardWithoutPrime;
  else if (cardWithPrime) finalPrice = cardWithPrime;
  else if (BLandTFWithoutPrime) finalPrice = BLandTFWithoutPrime;
  else if (BLandTFWithPrime) finalPrice = BLandTFWithPrime;
  else if (boletoPrice) finalPrice = boletoPrice;
  else finalPrice = price;

  return { finalPrice, discountType };
}
